import { Outlet } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { session } from '@/lib/session';

// This app IS the Operations plane. A tenant whose enabledProducts doesn't
// include 'operations' shouldn't be able to use it. See
// docs/PRODUCT_ENTITLEMENTS_DESIGN.md (zafroi-platform-app).
const THIS_PRODUCT = 'operations';
const ENGAGE_URL = 'https://engage.zafroi.com';
const WORKFORCE_URL = 'https://workforce.zafroi.com';

/**
 * Boot gate: blocks the whole Operations app for a tenant that isn't entitled
 * to it. Fail-open — PLATFORM_ADMIN (bounced to the platform app anyway) and a
 * missing/empty product set (a cached user from before the entitlement
 * rollout) both pass; only an explicit set that omits 'operations' is blocked.
 */
export function ProductGate() {
  const me = useCurrentUser();
  const products = me?.enabledProducts;
  const blocked =
    !!me &&
    me.tier !== 'PLATFORM_ADMIN' &&
    Array.isArray(products) &&
    products.length > 0 &&
    !products.includes(THIS_PRODUCT);

  if (!blocked) return <Outlet />;

  const has = (p: string) => (products ?? []).includes(p);
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="max-w-md space-y-2">
        <h1 className="text-xl font-semibold">Operations isn’t enabled for {me?.tenantName}</h1>
        <p className="text-sm text-muted-foreground">
          Your workspace isn’t subscribed to the Operations product. Switch to a
          product you have access to, or contact your administrator.
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {has('engage') ? (
          <a
            href={ENGAGE_URL}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Go to Engage
          </a>
        ) : null}
        {has('workforce') ? (
          <a
            href={WORKFORCE_URL}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium"
          >
            Go to Workforce
          </a>
        ) : null}
        <button
          type="button"
          onClick={() => session.clear()}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
